// Frontend Authentication Service
// Handles session token management for API calls

class AuthService {
  constructor() {
    this.refreshPromise = null;
    this.sessionToken = this.loadStoredSessionToken();
    
    // Get API base URL
    this.apiBaseUrl = import.meta.env.VITE_API_URL || 
      (import.meta.env.MODE === 'production' 
        ? 'https://good-feels-store-selector-api.fly.dev' 
        : 'http://localhost:3001');
    
    // Frontend auth key will be obtained from server endpoint
    this.frontendAuthKey = null;
  }

  loadStoredSessionToken() {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      return window.sessionStorage?.getItem('gf_sessionToken') || null;
    } catch (error) {
      if (import.meta.env.MODE !== 'production') {
        console.warn('Unable to access sessionStorage for auth token:', error);
      }
      return null;
    }
  }

  persistSessionToken(token) {
    this.sessionToken = token;

    if (typeof window === 'undefined') {
      return;
    }

    try {
      const storage = window.sessionStorage;
      if (!storage) {
        return;
      }

      if (token) {
        storage.setItem('gf_sessionToken', token);
      } else {
        storage.removeItem('gf_sessionToken');
      }
    } catch (error) {
      if (import.meta.env.MODE !== 'production') {
        console.warn('Unable to persist session token:', error);
      }
    }
  }
  
  async getFrontendAuthKey() {
    if (this.frontendAuthKey) {
      return this.frontendAuthKey;
    }
    
    try {
      // Get frontend auth key from server (this endpoint doesn't require auth)
      const response = await fetch(`${this.apiBaseUrl}/api/auth/frontend-key`, {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`Failed to get frontend key: ${response.status}`);
      }
      
      const { key } = await response.json();
      this.frontendAuthKey = key;
      return key;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Failed to get frontend auth key:', error);
      }
      throw new Error('Unable to authenticate with server');
    }
  }
  
  async createSession() {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        if (process.env.NODE_ENV !== 'production') {
          console.log('🔐 Creating new session token...');
        }
        const frontendKey = await this.getFrontendAuthKey();
        
        const response = await fetch(`${this.apiBaseUrl}/api/auth/session`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            frontendKey,
            userInfo: 'frontend-user'
          })
        });
        
        if (!response.ok) {
          this.persistSessionToken(null);
          const error = await response.json();
          throw new Error(`Session creation failed: ${error.message}`);
        }
        
        const { sessionToken } = await response.json();
        this.persistSessionToken(sessionToken);
        
        if (process.env.NODE_ENV !== 'production') {
          console.log('✅ New session created successfully');
        }
        return sessionToken;
      } catch (error) {
        this.persistSessionToken(null);
        if (process.env.NODE_ENV !== 'production') {
          console.error('❌ Session creation error:', error);
        }
        throw error;
      } finally {
        this.refreshPromise = null;
      }
    })();

    try {
      return await this.refreshPromise;
    } catch (error) {
      throw error;
    }
  }
  
  async ensureValidSession() {
    if (this.sessionToken) {
      return true;
    }

    try {
      await this.createSession();
      return true;
    } catch (error) {
      return false;
    }
  }
  
  async makeAuthenticatedRequest(url, options = {}) {
    try {
      // Ensure we have a valid session (sets header + cookie)
      await this.ensureValidSession();

      const originalHeaders = options.headers instanceof Headers
        ? Object.fromEntries(options.headers.entries())
        : { ...(options.headers || {}) };

      const fetchOptions = {
        ...options,
        credentials: 'include',
        headers: {
          ...originalHeaders
        }
      };

      if (this.sessionToken) {
        fetchOptions.headers['X-Session-Token'] = this.sessionToken;
      }

      if (process.env.NODE_ENV !== 'production') {
        console.log(`🌐 Making authenticated request to: ${url}`);
      }
      let response = await fetch(url, fetchOptions);

      // Handle authentication errors
      if (response.status === 401) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('🔒 Session expired, creating new session...');
        }
        
        // Create new session
        await this.createSession();
        if (this.sessionToken) {
          fetchOptions.headers['X-Session-Token'] = this.sessionToken;
        } else {
          delete fetchOptions.headers['X-Session-Token'];
        }
        
        if (process.env.NODE_ENV !== 'production') {
          console.log('🔄 Retrying request with new session...');
        }
        response = await fetch(url, fetchOptions);
      }
      
      return response;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ Authenticated request error:', error);
      }
      throw error;
    }
  }
  
  // Helper method for GET requests
  async get(endpoint, params = {}) {
    const url = new URL(`${this.apiBaseUrl}/api/${endpoint}`);
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        url.searchParams.append(key, params[key]);
      }
    });
    
    const response = await this.makeAuthenticatedRequest(url.toString());
    return response;
  }
  
  // Helper method for POST requests
  async post(endpoint, data = {}, options = {}) {
    const url = `${this.apiBaseUrl}/api/${endpoint}`;
    const postOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      body: JSON.stringify(data),
      ...options
    };
    
    const response = await this.makeAuthenticatedRequest(url, postOptions);
    return response;
  }
  
  // Clear session (logout)
  clearSession() {
    // Note: Cookies are cleared by making a request that would expire them,
    // but for simplicity, we just clear local state
    if (process.env.NODE_ENV !== 'production') {
      console.log('🚪 Session cleared (cookies will expire naturally)');
    }
    this.persistSessionToken(null);
  }
  
  // Check if we have a valid session (simplified, assumes cookies are valid)
  isAuthenticated() {
    return Boolean(this.sessionToken);
  }
}

// Create singleton instance
const authService = new AuthService();

export default authService;

// Export helper functions for easy use
export const authenticatedFetch = (url, options = {}) => {
  return authService.makeAuthenticatedRequest(url, options);
};

export const apiGet = (endpoint, params = {}) => {
  return authService.get(endpoint, params);
};

export const apiPost = (endpoint, data = {}, options = {}) => {
  return authService.post(endpoint, data, options);
};

export const clearAuth = () => {
  return authService.clearSession();
};

export const isAuthenticated = () => {
  return authService.isAuthenticated();
};
